import React from 'react';
import { CheckCircle2Icon, GitMergeIcon } from 'lucide-react';
import { WallCard } from './WallCard';
import { SlideToConfirm } from '../../ui/SlideToConfirm';
import { useDb } from '../../../hooks/useDb';
import { proposalDetail } from '../../../utils/dbSelectors';
import { euro } from '../../../utils/time';
import type { Proposal } from '../../../types/db';
interface ProposalFocusCardProps {
  proposal: Proposal;
  position: number;
  total: number;
  compact: boolean;
  onApprove: () => void;
  onReject: () => void;
  onEdit: () => void;
  onLater: () => void;
}
const SLIDE: Record<Proposal['kind'], {
  label: string;
  done: string;
}> = {
  mail: {
    label: 'Schuif om te versturen',
    done: 'Verstuurd'
  },
  code: {
    label: 'Schuif om goed te keuren',
    done: 'Goedgekeurd · mergen doe je zelf'
  },
  boekhouding: {
    label: 'Schuif om in te boeken',
    done: 'Ingeboekt'
  }
};
const btn = 'h-14 whitespace-nowrap rounded-full px-6 text-[22px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60';

/** Niveau 3, one at a time and large. Only slide-to-confirm is visible; the rest sits behind the bottom line. */
export function ProposalFocusCard({
  proposal,
  position,
  total,
  compact,
  onApprove,
  onReject,
  onEdit,
  onLater
}: ProposalFocusCardProps) {
  const detail = useDb(t => proposalDetail(t, proposal), [proposal]);
  const slide = SLIDE[proposal.kind];
  return <WallCard size="focus" opaque agent={proposal.agent} kicker={`voorstel ${position} van ${total}`} title={proposal.title} level="naOk" where={proposal.where} traceId={proposal.traceId} actions={<>
          <button type="button" onClick={onEdit} className={`${btn} border border-white/10 text-ink-soft hover:border-white/25 hover:text-ink`} data-id="element-1531" data-mp="9aw7bz3rcda0">
            Aanpassen
          </button>
          {total > 1 && <button type="button" onClick={onLater} className={`${btn} border border-white/10 text-ink-soft hover:border-white/25 hover:text-ink`} data-id="element-1532" data-mp="2b2j0a677mi94">
              Later
            </button>}
          <button type="button" onClick={onReject} className={`${btn} text-ink-muted hover:bg-white/[0.05] hover:text-ink`} data-id="element-1533" data-mp="j0g4yyipzxwu">
            Afwijzen
          </button>
        </>} data-id="element-1530" data-mp="48x4cqbxjidl">
      {detail.kind === 'mail' && <>
          <p className="text-ink-muted" data-id="element-1534" data-mp="svhsxr19etxu">
            Aan {detail.to} · {detail.subject}
          </p>
          <p className={`mt-2 text-ink ${compact ? 'line-clamp-2' : ''}`} data-id="element-1535" data-mp="wxlcz41zv6uq">“{detail.body}”</p>
        </>}

      {detail.kind === 'code' && <>
          <p className="text-ink" data-id="element-1536" data-mp="16pr7akfpy0d3">{detail.pr.title}</p>
          {!compact && <ul className="mt-3 space-y-1 font-mono text-[22px]" data-id="element-1537" data-mp="1bnaljkcf2d51">
              {detail.pr.files.map(f => <li key={f.path} className="flex justify-between gap-4" data-id="element-1538" data-mp="1goq8xj22gvql">
                  <span className="truncate" data-id="element-1539" data-mp="1ko8ri86f0uq9">{f.path}</span>
                  <span className="shrink-0 tabular-nums" data-id="element-1540" data-mp="1kmntu9ot0vo6">
                    <span className="text-positive" data-id="element-1541" data-mp="357uaxqm5daeb">+{f.additions}</span> <span className="text-negative" data-id="element-1542" data-mp="315hanhq664ox">−{f.deletions}</span>
                  </span>
                </li>)}
            </ul>}
          <p className="mt-3 flex items-center gap-2.5 text-positive" data-id="element-1543" data-mp="2w6mqocggua38">
            <CheckCircle2Icon className="h-6 w-6 shrink-0" aria-hidden data-id="element-1544" data-mp="2r8bi051wq5ml" />
            {detail.pr.tests.passed}/{detail.pr.tests.passed + detail.pr.tests.failed} tests geslaagd
          </p>
          <p className="mt-1 flex items-center gap-2.5 text-ink" data-id="element-1545" data-mp="3jvp4tabw5ksz">
            <GitMergeIcon className="h-6 w-6 shrink-0 text-ink-muted" aria-hidden data-id="element-1546" data-mp="3f58sjtjt4587" /> Mergen doe je zelf
          </p>
        </>}

      {detail.kind === 'boekhouding' && <>
          <ul className="space-y-1" data-id="element-1547" data-mp="3a4bb5j6mff7r">
            {detail.receipts.map(r => <li key={r.id} className="flex justify-between gap-4" data-id="element-1548" data-mp="bhglksv9e6dh">
                <span className="truncate text-ink" data-id="element-1549" data-mp="6odxh0993p4o">{r.vendor}</span>
                <span className="shrink-0 tabular-nums text-ink" data-id="element-1550" data-mp="3nd1ra72rlfrt">{euro(r.amount)}</span>
              </li>)}
          </ul>
          <p className="mt-3 tabular-nums" data-id="element-1551" data-mp="3rf6jr2hh86cv">
            Totaal {euro(detail.total)} · btw {euro(detail.vat)} · gekoppeld aan de bank
          </p>
        </>}

      <div className="mt-6" data-id="element-1552" data-mp="3idgqrs7y4zcz">
        <SlideToConfirm size="wall" label={slide.label} confirmedLabel={slide.done} onConfirm={onApprove} data-id="element-1553" data-mp="3ierz85cc6vaf" />
      </div>
    </WallCard>;
}